"use client";
// components/V0ChatPanel.tsx
// Full in-app chat panel — mirrors the configured v0 chat session.
// All v0 SDK calls run server-side; the panel communicates via SSE streaming
// and a history endpoint so the API token is never exposed to the browser.

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { PhoneResultCard, type PhoneData } from "./chat/PhoneResultCard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatFile {
  name: string;
  content: string;
}

interface AttachedImage {
  name: string;
  dataUrl: string; // base64 data URL
}

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  files?: ChatFile[];
  images?: AttachedImage[]; // user-attached images
  demo?: string | null;
  phoneResult?: PhoneData | null; // phone lookup result from AI
  timestamp: number;
  failed?: boolean; // true when the send job errored
}

interface PullResult {
  written: string[];
  skipped: { name: string; reason: string }[];
}

type GitPullState = "idle" | "pending" | "success" | "error" | "vercel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(ts: number) {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FileDiff({ file, defaultOpen }: { file: ChatFile; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <div style={styles.diffCard}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={styles.diffToggle}
        aria-expanded={open}
      >
        <span style={styles.diffIcon}>{open ? "▾" : "▸"}</span>
        <span style={styles.diffFileName}>{file.name}</span>
        <span style={styles.diffLineCount}>
          {file.content.split("\n").length} lines
        </span>
      </button>
      {open && (
        <pre style={styles.codeBlock}>
          <code>{file.content}</code>
        </pre>
      )}
    </div>
  );
}

function AssistantBubble({ message }: { message: ChatMessage }) {
  const [pullState, setPullState] = useState<
    "idle" | "loading" | "success" | "error" | "unavailable"
  >("idle");
  const [pullResult, setPullResult] = useState<PullResult | null>(null);

  const handlePull = useCallback(async () => {
    if (!message.files?.length) return;
    setPullState("loading");
    try {
      const res = await fetch("/api/v0-pull", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: message.files }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          setPullState("unavailable");
        } else {
          setPullState("error");
        }
        return;
      }
      setPullResult(data);
      setPullState("success");
    } catch {
      setPullState("error");
    }
  }, [message.files]);

  return (
    <div style={styles.assistantBubbleWrapper}>
      <div style={styles.assistantAvatar} aria-hidden="true">
        v0
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={styles.bubbleText}>{message.text}</p>

        {/* Phone result card */}
        {message.phoneResult && (
          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <PhoneResultCard phone={message.phoneResult} />
          </div>
        )}

        {/* Files */}
        {message.files && message.files.length > 0 && (
          <div style={styles.filesSection}>
            <p style={styles.sectionLabel}>
              {message.files.length} file{message.files.length !== 1 ? "s" : ""} generated
            </p>
            {message.files.map((f) => (
              <FileDiff key={f.name} file={f} />
            ))}

            {/* Pull to disk */}
            <div style={styles.pullRow}>
              <button
                onClick={handlePull}
                disabled={pullState === "loading" || pullState === "success"}
                style={{
                  ...styles.pullButton,
                  ...(pullState === "success" ? styles.pullButtonSuccess : {}),
                  ...(pullState === "error" || pullState === "unavailable"
                    ? styles.pullButtonError
                    : {}),
                }}
              >
                {pullState === "loading" && "Writing files…"}
                {pullState === "idle" && "Pull to local disk"}
                {pullState === "success" && "Written — HMR will pick up changes"}
                {pullState === "error" && "Write failed — check console"}
                {pullState === "unavailable" &&
                  "Not available on Vercel — run locally to pull"}
              </button>

              {pullState === "success" && pullResult && (
                <div style={styles.pullSummary}>
                  {pullResult.written.length > 0 && (
                    <span style={styles.pullWritten}>
                      Written: {pullResult.written.join(", ")}
                    </span>
                  )}
                  {pullResult.skipped.length > 0 && (
                    <span style={styles.pullSkipped}>
                      Skipped: {pullResult.skipped.map((s) => s.name).join(", ")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* v0 demo link — open in new tab only, no iframe (app itself is the live preview) */}
        {message.demo && (
          <a
            href={message.demo}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.demoLink}
          >
            Open v0 preview
          </a>
        )}

        <span style={styles.timestamp}>{formatTime(message.timestamp)}</span>
      </div>
    </div>
  );
}

function UserBubble({ message }: { message: ChatMessage }) {
  return (
    <div style={styles.userBubbleWrapper}>
      <div
        style={{
          ...styles.userBubble,
          ...(message.failed
            ? { borderColor: "rgba(239,68,68,0.5)", background: "rgba(239,68,68,0.08)" }
            : {}),
        }}
      >
        {/* Attached images */}
        {message.images && message.images.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {message.images.map((img, i) => (
              <img
                key={i}
                src={img.dataUrl}
                alt={img.name}
                style={{
                  maxWidth: 180,
                  maxHeight: 120,
                  borderRadius: 6,
                  objectFit: "cover",
                  border: "1px solid rgba(255,255,255,0.15)",
                  opacity: message.failed ? 0.6 : 1,
                }}
              />
            ))}
          </div>
        )}
        <p style={styles.userBubbleText}>{message.text}</p>
        {message.failed ? (
          <span
            style={{
              display: "block",
              marginTop: 4,
              fontSize: 11,
              color: "#f87171",
              fontStyle: "italic",
            }}
            role="alert"
          >
            Not delivered — an error occurred
          </span>
        ) : (
          <span style={styles.userTimestamp}>{formatTime(message.timestamp)}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

type HistoryState = "loading" | "ready" | "error" | "unconfigured";

interface V0ChatPanelProps {
  /** Explicit height for the panel. Defaults to "600px". Pass "100%" when the parent already has a fixed height. */
  height?: React.CSSProperties["height"];
  /** Explicit width for the panel. Defaults to "100%". */
  width?: React.CSSProperties["width"];
}

export function V0ChatPanel({ height = "600px", width = "100%" }: V0ChatPanelProps) {
  const PAGE_SIZE = 20;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [historyState, setHistoryState] = useState<HistoryState>("loading");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [gitPullState, setGitPullState] = useState<GitPullState>("idle");
  const [gitPullOutput, setGitPullOutput] = useState<string | null>(null);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch history on mount + resume any pending job ──────────���────────────
  useEffect(() => {
    let cancelled = false;

    async function loadHistoryAndState() {
      try {
        // Single request — history response now includes job state so we
        // don't need a separate /api/v0-chat/state call.
        const res = await fetch(`/api/v0-chat/history?limit=${PAGE_SIZE}&offset=0`);
        if (cancelled) return;
        if (res.status === 503) { setHistoryState("unconfigured"); return; }
        if (!res.ok) throw new Error(`History fetch failed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;

        setMessages(data.messages ?? []);
        setHasMore(data.hasMore ?? false);
        setCurrentOffset(PAGE_SIZE);
        setHistoryState("ready");

        // Resume any pending job returned inline — no extra round trip.
        if (data.isLoading && data.jobId && data.startedAt) {
          setLoading(true);
          setLoadingStartedAt(data.startedAt);
          setElapsedSeconds(Math.floor((Date.now() - data.startedAt) / 1000));
          pollJob(data.jobId, data.startedAt);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message ?? "Failed to load chat history");
          setHistoryState("error");
        }
      }
    }

    loadHistoryAndState();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Elapsed seconds counter while loading ──────────────────────────────────
  useEffect(() => {
    if (!loading) {
      setElapsedSeconds(0);
      setLoadingStartedAt(null);
      return;
    }
    // loadingStartedAt may already be set when resuming from localStorage.
    const start = loadingStartedAt ?? Date.now();
    if (!loadingStartedAt) setLoadingStartedAt(start);
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Auto-resize textarea ───────────────────────────────────────────────────
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }, [input]);

  // ── Git pull ───────────────────────────────────────────────────────────────
  const triggerGitPull = useCallback(async () => {
    setGitPullState("pending");
    setGitPullOutput(null);
    try {
      const res = await fetch("/api/git/pull", { method: "POST" });
      const data = await res.json();
      if (res.status === 403) {
        setGitPullState("vercel");
      } else if (!res.ok) {
        setGitPullState("error");
        setGitPullOutput(data.error ?? "git pull failed");
      } else {
        setGitPullState("success");
        setGitPullOutput(data.output ?? "Already up to date.");
      }
    } catch {
      setGitPullState("error");
      setGitPullOutput("Could not reach /api/git/pull");
    }
  }, []);

  // ── Poll job status until done or error ────────────────────────────────────
  const pollJob = useCallback(
    (jobId: string, resumedStartedAt?: number) => {
      const INTERVAL = 3000;
      const MAX_WAIT_MS = 5 * 60 * 1000; // 5 minutes
      const startedAt = resumedStartedAt ?? Date.now();
      // Allow a few consecutive 404s before giving up — the server process may
      // have briefly restarted and the in-memory job store was cleared.
      const MAX_NOT_FOUND = 3;
      let notFoundCount = 0;

      const markFailed = (message: string) => {
        setLoading(false);
        setError(message);
        setMessages((prev) => {
          const idx = [...prev].reverse().findIndex((m) => m.role === "user");
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;
          return prev.map((m, i) => (i === realIdx ? { ...m, failed: true } : m));
        });
      };

      const interval = setInterval(async () => {
        try {
          if (Date.now() - startedAt > MAX_WAIT_MS) {
            clearInterval(interval);
            markFailed("v0 took too long to respond. Please try again.");
            return;
          }

          const res = await fetch(`/api/v0-chat/status?jobId=${jobId}`);
          const data = await res.json();

          if (res.status === 404) {
            // Job not found — could be a process restart wiping the in-memory store.
            // Give it a few more polls before treating as a hard failure.
            notFoundCount += 1;
            if (notFoundCount >= MAX_NOT_FOUND) {
              clearInterval(interval);
              markFailed("Message failed to send — the server lost track of the job. Please try again.");
            }
            return;
          }

          // Reset counter on any non-404 response.
          notFoundCount = 0;

          if (data.status === "done") {
            clearInterval(interval);
            const assistantMsg: ChatMessage = {
              role: "assistant",
              text: data.text ?? "",
              files: data.files ?? [],
              demo: data.demo ?? null,
              phoneResult: data.phoneResult ?? null,
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, assistantMsg]);
            setLoading(false);
            if (data.triggerGitPull) triggerGitPull();
          } else if (data.status === "error") {
            clearInterval(interval);
            markFailed(data.error ?? "v0 returned an error");
          }
          // status === "pending" — keep polling
        } catch {
          // Network blip — keep polling, don't abort
        }
      }, INTERVAL);
    },
    [triggerGitPull]
  );

  // ── Send message — fire and forget, then poll for result ───────────────────
  const sendMessage = useCallback(
    async (e: React.FormEvent | React.KeyboardEvent) => {
      e.preventDefault();
      const text = input.trim();
      if ((!text && attachedImages.length === 0) || loading) return;

      // Snapshot images before clearing state
      const imagesToSend = [...attachedImages];

      // Build attachments array for images — the v0 SDK requires { url, name?, contentType? }.
      // We pass the data URL directly as `url`; the SDK accepts data: URIs.
      const attachments = imagesToSend.map((img) => {
        const contentType = img.dataUrl.split(";")[0].replace("data:", "");
        return { url: img.dataUrl, name: img.name, contentType };
      });

      setMessages((prev) => [
        ...prev,
        { role: "user", text: text || "(image attached)", images: imagesToSend, timestamp: Date.now() },
      ]);
      setInput("");
      setAttachedImages([]);
      setLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/v0-chat/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text || " ",
            attachments: attachments.length > 0 ? attachments : undefined,
          }),
        });

        const rawBody = await res.text();
        let data: any = {};
        try {
          data = JSON.parse(rawBody);
        } catch {
          throw new Error(`Server returned non-JSON response (${res.status}): ${rawBody.slice(0, 120)}`);
        }

        if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);

        // Use the server's startedAt as the canonical timer start.
        const startedAt: number = data.startedAt ?? Date.now();
        setLoadingStartedAt(startedAt);
        pollJob(data.jobId, startedAt);
      } catch (err: any) {
        setLoading(false);
        setError(err.message ?? "Something went wrong");
        // Mark the last user message as failed so the bubble shows inline feedback.
        setMessages((prev) => {
          const idx = [...prev].reverse().findIndex((m) => m.role === "user");
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;
          return prev.map((m, i) => (i === realIdx ? { ...m, failed: true } : m));
        });
      }
    },
    [input, attachedImages, loading, pollJob]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) sendMessage(e);
  };

  // ── Load older messages (prepend previous page) ────────────────────────────
  const loadOlderMessages = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/v0-chat/history?limit=${PAGE_SIZE}&offset=${currentOffset}`);
      if (!res.ok) throw new Error("Failed to load older messages");
      const data = await res.json();
      setMessages((prev) => [...(data.messages ?? []), ...prev]);
      setHasMore(data.hasMore ?? false);
      setCurrentOffset((o) => o + PAGE_SIZE);
    } catch {
      // non-critical — silently ignore
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentOffset]);

  // ── Image attachment ───────────────────────────────────────────────────────
  const handleImageFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const allowed = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 4);
    allowed.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        setAttachedImages((prev) => {
          if (prev.length >= 4) return prev; // max 4
          return [...prev, { name: file.name, dataUrl }];
        });
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const removeImage = useCallback((index: number) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Paste images from clipboard ────────────────────────────────────────────
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = Array.from(items)
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean) as File[];
    if (imageFiles.length > 0) {
      const dt = new DataTransfer();
      imageFiles.forEach((f) => dt.items.add(f));
      handleImageFiles(dt.files);
    }
  }, [handleImageFiles]);

  const resetChat = () => {
    // Reset only clears local state — the server chat session persists.
    setMessages([]);
    setError(null);
    setInput("");
    setLoading(false);
    setGitPullState("idle");
    setGitPullOutput(null);
    // Reload history from server (last PAGE_SIZE only).
    setHistoryState("loading");
    setHasMore(false);
    setCurrentOffset(PAGE_SIZE);
    fetch(`/api/v0-chat/history?limit=${PAGE_SIZE}&offset=0`)
      .then((r) => r.json())
      .then((data) => {
        setMessages(data.messages ?? []);
        setHasMore(data.hasMore ?? false);
        setHistoryState("ready");
      })
      .catch(() => setHistoryState("error"));
  };

  return (
    <div
      style={{ ...styles.root, height, width }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        handleImageFiles(e.dataTransfer.files);
      }}
    >
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div
            style={{
              ...styles.headerDot,
              background:
                historyState === "ready"
                  ? C.success
                  : historyState === "loading"
                  ? C.accent
                  : C.error,
            }}
            aria-hidden="true"
          />
          <span style={styles.headerTitle}>v0 Dev Chat</span>
          <span
            style={{
              ...styles.chatIdBadge,
              color:
                historyState === "ready"
                  ? C.success
                  : historyState === "unconfigured"
                  ? C.error
                  : C.muted,
            }}
          >
            {historyState === "loading" && "loading history…"}
            {historyState === "ready" && "mirrored"}
            {historyState === "error" && "history error"}
            {historyState === "unconfigured" && "V0_CHAT_ID not set"}
          </span>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={triggerGitPull}
            disabled={gitPullState === "pending"}
            style={{
              ...styles.syncButton,
              ...(gitPullState === "pending" ? styles.syncButtonDisabled : {}),
              ...(gitPullState === "success" ? styles.syncButtonSuccess : {}),
              ...(gitPullState === "error" ? styles.syncButtonError : {}),
            }}
            title="Pull latest changes from GitHub to local disk"
          >
            {gitPullState === "pending" ? "Syncing…" : "Sync"}
          </button>
          {messages.length > 0 && (
            <button onClick={resetChat} style={styles.resetButton}>
              New chat
            </button>
          )}
        </div>
      </header>

      {/* Messages */}
      <main style={styles.messageList} role="log" aria-live="polite" aria-label="Chat messages">
        {/* Load older messages button — shown at top when there are more */}
        {hasMore && historyState === "ready" && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
            <button
              onClick={loadOlderMessages}
              disabled={loadingMore}
              style={{
                background: "transparent",
                border: `1px solid ${styles.diffCard.borderColor ?? "#333"}`,
                color: loadingMore ? "#666" : "#aaa",
                borderRadius: 6,
                padding: "5px 16px",
                fontSize: 12,
                cursor: loadingMore ? "default" : "pointer",
              }}
            >
              {loadingMore ? "Loading…" : "Load older messages"}
            </button>
          </div>
        )}

        {messages.length === 0 && historyState === "loading" && (
          <div style={styles.emptyState}>
            <p style={styles.emptySubtitle}>Loading chat history…</p>
          </div>
        )}

        {messages.length === 0 && historyState === "unconfigured" && (
          <div style={styles.emptyState}>
            <p style={styles.emptyTitle}>Chat not configured</p>
            <p style={styles.emptySubtitle}>
              Set <code style={styles.inlineCode}>V0_CHAT_ID</code> in your{" "}
              <code style={styles.inlineCode}>.env.local</code> to mirror an
              existing v0 chat session. You can find the chat ID in the URL of
              any v0 chat.
            </p>
          </div>
        )}

        {messages.length === 0 && historyState === "ready" && (
          <div style={styles.emptyState}>
            <p style={styles.emptyTitle}>Mirrored chat ready</p>
            <p style={styles.emptySubtitle}>
              This chat mirrors your v0 session. Every message you send here is
              forwarded server-side — your API token never leaves the server.
              Changes pushed by v0 will trigger an automatic{" "}
              <code style={styles.inlineCode}>git pull</code> when running
              locally.
            </p>
          </div>
        )}

        {messages.map((msg, i) =>
          msg.role === "user" ? (
            <UserBubble key={i} message={msg} />
          ) : (
            <AssistantBubble key={i} message={msg} />
          )
        )}

        {/* Typing indicator while waiting for v0 response */}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={styles.typingIndicator} aria-label="v0 is thinking">
              <div style={styles.assistantAvatar} aria-hidden="true">v0</div>
              <div style={styles.typingDots}>
                <span style={styles.dot} />
                <span style={{ ...styles.dot, animationDelay: "0.15s" }} />
                <span style={{ ...styles.dot, animationDelay: "0.3s" }} />
              </div>
            </div>
            <div style={styles.elapsedTimer} aria-live="polite">
              {elapsedSeconds}s
            </div>
          </div>
        )}

        {error && (
          <div style={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </main>

      {/* Git sync status bar — always visible */}
      <div
        style={{
          ...styles.gitBar,
          ...(gitPullState === "success" ? styles.gitBarSuccess : {}),
          ...(gitPullState === "error" ? styles.gitBarError : {}),
          ...(gitPullState === "vercel" ? styles.gitBarVercel : {}),
          ...(gitPullState === "idle" ? styles.gitBarIdle : {}),
        }}
        role="status"
        aria-live="polite"
      >
        {/* Indicator dot */}
        <span
          style={{
            ...styles.gitBarIndicator,
            ...(gitPullState === "success" ? { background: C.success } : {}),
            ...(gitPullState === "error" ? { background: C.error } : {}),
            ...(gitPullState === "pending" ? { background: C.accent, opacity: 0.5 } : {}),
            ...(gitPullState === "vercel" ? { background: C.muted } : {}),
          }}
          aria-hidden="true"
        />

        <div style={styles.gitBarContent}>
          {gitPullState === "idle" && (
            <span>Local sync ready — click Sync or send a prompt with file changes.</span>
          )}
          {gitPullState === "pending" && (
            <span>Waiting for GitHub to register the push, then running <code style={styles.gitBarCode}>git pull</code>…</span>
          )}
          {gitPullState === "success" && (
            <span>
              Sync complete — HMR will pick up changes.
              {gitPullOutput && gitPullOutput !== "Already up to date." && (
                <code style={styles.gitBarCode}> {gitPullOutput.trim()}</code>
              )}
              {gitPullOutput === "Already up to date." && (
                <span style={{ color: C.muted }}> Already up to date.</span>
              )}
            </span>
          )}
          {gitPullState === "error" && (
            <span>
              Sync failed.{" "}
              {gitPullOutput && (
                <code style={styles.gitBarCode}>{gitPullOutput.trim()}</code>
              )}
              <button
                onClick={triggerGitPull}
                style={styles.gitBarRetry}
              >
                Retry
              </button>
            </span>
          )}
          {gitPullState === "vercel" && (
            <span>
              Sync skipped — <code style={styles.gitBarCode}>git pull</code> only runs on your local dev server, not on Vercel.
            </span>
          )}
        </div>
      </div>

      {/* Input */}
      <footer style={styles.inputArea}>
        {/* Image preview strip */}
        {attachedImages.length > 0 && (
          <div style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            padding: "8px 12px 0",
          }}>
            {attachedImages.map((img, i) => (
              <div key={i} style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={img.dataUrl}
                  alt={img.name}
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    display: "block",
                  }}
                />
                <button
                  onClick={() => removeImage(i)}
                  aria-label={`Remove ${img.name}`}
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: C.error,
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: "18px",
                    textAlign: "center",
                    padding: 0,
                  }}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={sendMessage} style={styles.inputForm}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleImageFiles(e.target.files)}
            aria-label="Attach image"
          />
          {/* Attach button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || attachedImages.length >= 4}
            title="Attach image (or paste from clipboard)"
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              color: attachedImages.length >= 4 ? C.muted : C.muted,
              borderRadius: 6,
              padding: "0 10px",
              fontSize: 16,
              cursor: attachedImages.length >= 4 ? "not-allowed" : "pointer",
              flexShrink: 0,
              height: 36,
              display: "flex",
              alignItems: "center",
              opacity: attachedImages.length >= 4 ? 0.4 : 1,
            }}
            aria-label="Attach image"
          >
            {/* paperclip icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Describe what you want to build… (Enter to send, Shift+Enter for newline)"
            style={styles.textarea}
            rows={1}
            disabled={loading}
            aria-label="Message input"
          />
          <button
            type="submit"
            disabled={loading || (!input.trim() && attachedImages.length === 0)}
            style={{
              ...styles.sendButton,
              ...(loading || (!input.trim() && attachedImages.length === 0) ? styles.sendButtonDisabled : {}),
            }}
            aria-label="Send message"
          >
            {loading ? "…" : "Send"}
          </button>
        </form>
        <p style={styles.hint}>
          Attach images via the paperclip, paste from clipboard, or drag and drop.
        </p>
      </footer>

      {/* Keyframe animation injected inline */}
      <style>{`
        @keyframes v0-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes v0-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles (inline — no Tailwind dependency, works in any context)
// ---------------------------------------------------------------------------

const C = {
  bg: "#0a0a0a",
  surface: "#111111",
  surfaceHover: "#1a1a1a",
  border: "#222222",
  text: "#ededed",
  muted: "#888888",
  accent: "#6366f1",
  accentHover: "#818cf8",
  success: "#22c55e",
  error: "#ef4444",
  userBg: "#6366f1",
  userText: "#ffffff",
  codeBg: "#0d0d0d",
  codeText: "#a5f3fc",
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    // height and width are injected at render time from props — do not set here.
    minHeight: 0,
    overflow: "hidden",
    background: C.bg,
    color: C.text,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    fontSize: 14,
    lineHeight: 1.6,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
  },

  // Header
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
    background: C.surface,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: C.accent,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: "0.01em",
    color: C.text,
  },
  chatIdBadge: {
    fontSize: 11,
    color: C.muted,
    background: "#1a1a1a",
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: "1px 6px",
    fontFamily: "monospace",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  syncButton: {
    background: "transparent",
    border: `1px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "border-color 0.15s, color 0.15s",
  },
  syncButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  syncButtonSuccess: {
    border: `1px solid ${C.success}`,
    color: C.success,
  },
  syncButtonError: {
    border: `1px solid ${C.error}`,
    color: C.error,
  },
  resetButton: {
    background: "transparent",
    border: `1px solid ${C.border}`,
    color: C.muted,
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    cursor: "pointer",
  },

  // Messages
  messageList: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 16px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minHeight: 0,
  },

  // Empty state
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 40,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: C.text,
    margin: 0,
  },
  emptySubtitle: {
    fontSize: 14,
    color: C.muted,
    maxWidth: 480,
    margin: 0,
    lineHeight: 1.7,
  },

  // User bubble
  userBubbleWrapper: {
    display: "flex",
    justifyContent: "flex-end",
  },
  userBubble: {
    background: C.userBg,
    color: C.userText,
    borderRadius: "16px 16px 4px 16px",
    padding: "10px 14px",
    maxWidth: "72%",
  },
  userBubbleText: {
    margin: 0,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  userTimestamp: {
    display: "block",
    fontSize: 10,
    opacity: 0.7,
    marginTop: 4,
    textAlign: "right",
  },

  // Assistant bubble
  assistantBubbleWrapper: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  assistantAvatar: {
    width: 30,
    height: 30,
    borderRadius: 6,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    letterSpacing: "0.05em",
  },
  bubbleText: {
    margin: "0 0 8px",
    color: C.text,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  streamCursor: {
    display: "inline-block",
    width: 2,
    height: "1em",
    background: C.accent,
    marginLeft: 2,
    verticalAlign: "text-bottom",
    animation: "v0-blink 1s step-end infinite",
  },
  inlineCode: {
    fontFamily: "monospace",
    fontSize: 12,
    background: "rgba(255,255,255,0.07)",
    padding: "1px 5px",
    borderRadius: 3,
    color: C.codeText,
  },
  timestamp: {
    fontSize: 10,
    color: C.muted,
    display: "block",
    marginTop: 6,
  },

  // Files
  filesSection: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 10,
  },
  sectionLabel: {
    margin: "0 0 4px",
    fontSize: 11,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  diffCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    overflow: "hidden",
    background: C.surface,
  },
  diffToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "8px 12px",
    background: "transparent",
    border: "none",
    color: C.text,
    cursor: "pointer",
    textAlign: "left",
    fontSize: 13,
  },
  diffIcon: {
    color: C.muted,
    fontSize: 12,
    width: 12,
  },
  diffFileName: {
    flex: 1,
    fontFamily: "monospace",
    fontSize: 12,
    color: C.codeText,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  diffLineCount: {
    fontSize: 10,
    color: C.muted,
    flexShrink: 0,
  },
  codeBlock: {
    margin: 0,
    padding: 12,
    background: C.codeBg,
    color: C.codeText,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: 11,
    lineHeight: 1.7,
    overflowX: "auto",
    whiteSpace: "pre",
    maxHeight: 360,
    overflowY: "auto",
    borderTop: `1px solid ${C.border}`,
  },

  // Pull
  pullRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 8,
  },
  pullButton: {
    background: C.surface,
    border: `1px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 6,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.15s",
  },
  pullButtonSuccess: {
    border: `1px solid ${C.success}`,
    color: C.success,
    cursor: "default",
  },
  pullButtonError: {
    border: `1px solid ${C.error}`,
    color: C.error,
    cursor: "default",
  },
  pullSummary: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11,
    fontFamily: "monospace",
  },
  pullWritten: {
    color: C.success,
  },
  pullSkipped: {
    color: C.error,
  },

  // Demo
  demoSection: {
    marginBottom: 10,
    borderRadius: 6,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
  },
  demoHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
  },
  demoLink: {
    color: C.accent,
    fontSize: 11,
    textDecoration: "none",
  },
  iframe: {
    width: "100%",
    height: 420,
    border: "none",
    display: "block",
    background: "#fff",
  },

  // Typing
  typingIndicator: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  elapsedTimer: {
    fontSize: 11,
    color: C.muted,
    paddingLeft: 42, // aligns under the typing bubble, past the avatar width
  },
  typingDots: {
    display: "flex",
    gap: 4,
    alignItems: "center",
    padding: "10px 14px",
    background: C.surface,
    borderRadius: "16px 16px 16px 4px",
    border: `1px solid ${C.border}`,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: C.accent,
    animation: "v0-bounce 1s infinite",
    display: "inline-block",
  },

  // Error
  errorBanner: {
    background: "#1a0000",
    border: `1px solid ${C.error}`,
    color: C.error,
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 13,
  },

  // Input
  inputArea: {
    borderTop: `1px solid ${C.border}`,
    padding: "12px 16px",
    flexShrink: 0,
    background: C.surface,
  },
  inputForm: {
    display: "flex",
    gap: 8,
    alignItems: "flex-end",
  },
  textarea: {
    flex: 1,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text,
    padding: "10px 12px",
    fontSize: 13,
    fontFamily: "inherit",
    resize: "none",
    lineHeight: 1.5,
    outline: "none",
    minHeight: 40,
    maxHeight: 160,
    overflowY: "auto",
  },
  sendButton: {
    background: C.accent,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
    height: 42,
  },
  sendButtonDisabled: {
    background: "#2a2a3a",
    color: C.muted,
    cursor: "not-allowed",
  },
  hint: {
    margin: "6px 0 0",
    fontSize: 11,
    color: C.muted,
  },

  // Git sync status bar
  gitBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 16px",
    fontSize: 12,
    borderTop: `1px solid ${C.border}`,
    color: C.muted,
    flexShrink: 0,
    background: "#0d1117",
    minHeight: 34,
  },
  gitBarIdle: {
    color: C.muted,
  },
  gitBarSuccess: {
    color: C.success,
    background: "#001a0a",
    borderTop: `1px solid #14532d`,
  },
  gitBarError: {
    color: C.error,
    background: "#1a0000",
    borderTop: `1px solid #7f1d1d`,
  },
  gitBarVercel: {
    color: C.muted,
    background: "#0d1117",
  },
  gitBarIndicator: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: C.muted,
    flexShrink: 0,
    transition: "background 0.2s",
  },
  gitBarContent: {
    flex: 1,
    lineHeight: 1.5,
  },
  gitBarCode: {
    fontFamily: "monospace",
    fontSize: 11,
    background: "rgba(255,255,255,0.07)",
    padding: "1px 5px",
    borderRadius: 3,
  },
  gitBarRetry: {
    marginLeft: 10,
    background: "transparent",
    border: `1px solid ${C.error}`,
    color: C.error,
    borderRadius: 4,
    padding: "1px 8px",
    fontSize: 11,
    cursor: "pointer",
  },
};
